import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { wsService } from "../api/websocketService";
import type {
  GamificationBadgeEarnedPayload,
  GamificationPointsUpdatedPayload,
  GamificationStreakUpdatedPayload,
  GamificationMilestoneReachedPayload,
  WSMessage,
} from "../types/websocket.types";
import {
  gamificationBadgeProgressQueryKey,
  gamificationBadgesQueryKey,
  gamificationHistoryQueryKey,
  gamificationPointsQueryKey,
  gamificationRankingsQueryKey,
  gamificationSummaryQueryKey,
} from "./useGamificationQueries";

interface CooldownEntry<T> {
  key: T;
  at: number;
}

function isDuplicateWithinCooldown<T>(
  previous: CooldownEntry<T> | null,
  key: T,
  now: number,
  cooldownMs: number,
  equals: (left: T, right: T) => boolean,
): boolean {
  return !!previous && equals(previous.key, key) && now - previous.at < cooldownMs;
}

export interface GamificationRankChange {
  delta: number;
  currentPosition?: number;
  previousPosition?: number;
}

export interface GamificationBadgeEarned {
  id: string;
  name: string;
  description: string;
  tier: string;
  type: string;
  pointsBonus: number;
  earnedAt: string;
}

export interface GamificationStreakUpdated {
  previousCurrentStreak: number;
  currentStreak: number;
  previousLongestStreak: number;
  longestStreak: number;
  bonusAwarded: boolean;
  bonusType?: "WEEKLY" | "MONTHLY";
  updatedAt: string;
}

export interface GamificationMilestoneReached {
  points: number;
  label: string;
  previousTotalPoints: number;
  currentTotalPoints: number;
  reachedAt: string;
}

interface UseGamificationRealtimeSyncOptions {
  terreiroId?: string;
  mediumId?: string;
  enabled?: boolean;
  onRankChange?: (change: GamificationRankChange) => void;
  onBadgeEarned?: (badge: GamificationBadgeEarned) => void;
  onStreakUpdated?: (streak: GamificationStreakUpdated) => void;
  onMilestoneReached?: (milestone: GamificationMilestoneReached) => void;
  rankChangeCooldownMs?: number;
  milestoneCooldownMs?: number;
}

export function useGamificationRealtimeSync({
  terreiroId,
  mediumId,
  enabled = true,
  onRankChange,
  onBadgeEarned,
  onStreakUpdated,
  onMilestoneReached,
  rankChangeCooldownMs = 5000,
  milestoneCooldownMs = 8000,
}: UseGamificationRealtimeSyncOptions): void {
  const queryClient = useQueryClient();
  const lastRankChangeRef = useRef<{
    delta: number;
    currentPosition?: number;
    at: number;
  } | null>(null);
  const lastMilestoneRef = useRef<CooldownEntry<{ points: number }> | null>(
    null,
  );

  useEffect(() => {
    lastRankChangeRef.current = null;
    lastMilestoneRef.current = null;
  }, [terreiroId, mediumId]);

  useEffect(() => {
    if (!enabled || !terreiroId || !mediumId) {
      return;
    }

    const unsubscribePointsUpdated = wsService.onGamificationPointsUpdated(
      (message) => {
      const payload = (message as WSMessage<GamificationPointsUpdatedPayload>)
        .payload;
      if (!payload) return;

      if (payload.scope.terreiroId !== terreiroId) return;

      const isCurrentMedium = payload.scope.mediumId === mediumId;

      queryClient.invalidateQueries({ queryKey: ["gamification", "rankings"] });

      if (!isCurrentMedium) return;

      if (payload.rank && typeof payload.rank.delta === "number" && payload.rank.delta !== 0) {
        const now = Date.now();
        const isDuplicate = isDuplicateWithinCooldown(
          lastRankChangeRef.current
            ? {
                key: {
                  delta: lastRankChangeRef.current.delta,
                  currentPosition: lastRankChangeRef.current.currentPosition,
                },
                at: lastRankChangeRef.current.at,
              }
            : null,
          {
            delta: payload.rank.delta,
            currentPosition: payload.rank.currentPosition,
          },
          now,
          rankChangeCooldownMs,
          (left, right) =>
            left.delta === right.delta &&
            left.currentPosition === right.currentPosition,
        );

        if (!isDuplicate) {
          onRankChange?.({
            delta: payload.rank.delta,
            currentPosition: payload.rank.currentPosition,
            previousPosition: payload.rank.previousPosition,
          });

          lastRankChangeRef.current = {
            delta: payload.rank.delta,
            currentPosition: payload.rank.currentPosition,
            at: now,
          };
        }
      }

      queryClient.invalidateQueries({
        queryKey: gamificationPointsQueryKey(terreiroId, mediumId),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationHistoryQueryKey(mediumId),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationBadgeProgressQueryKey(terreiroId, mediumId),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationSummaryQueryKey(terreiroId, mediumId),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationBadgesQueryKey(mediumId),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationRankingsQueryKey("TERREIRO", terreiroId, "WEEKLY"),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationRankingsQueryKey("TERREIRO", terreiroId, "MONTHLY"),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationRankingsQueryKey("TERREIRO", terreiroId, "YEARLY"),
      });
      queryClient.invalidateQueries({
        queryKey: gamificationRankingsQueryKey("TERREIRO", terreiroId, "ALL_TIME"),
      });
      },
    );

    const unsubscribeBadgeEarned = wsService.onGamificationBadgeEarned(
      (message) => {
        const payload = (message as WSMessage<GamificationBadgeEarnedPayload>)
          .payload;
        if (!payload) return;
        if (payload.scope.terreiroId !== terreiroId) return;
        if (payload.scope.mediumId !== mediumId) return;

        onBadgeEarned?.({
          id: payload.badge.id,
          name: payload.badge.name,
          description: payload.badge.description,
          tier: payload.badge.tier,
          type: payload.badge.type,
          pointsBonus: payload.badge.pointsBonus,
          earnedAt: payload.badge.earnedAt,
        });

        queryClient.invalidateQueries({
          queryKey: gamificationBadgesQueryKey(mediumId),
        });
        queryClient.invalidateQueries({
          queryKey: gamificationBadgeProgressQueryKey(terreiroId, mediumId),
        });
        queryClient.invalidateQueries({
          queryKey: gamificationSummaryQueryKey(terreiroId, mediumId),
        });
      },
    );

    const unsubscribeStreakUpdated = wsService.onGamificationStreakUpdated(
      (message) => {
        const payload = (message as WSMessage<GamificationStreakUpdatedPayload>)
          .payload;
        if (!payload) return;
        if (payload.scope.terreiroId !== terreiroId) return;
        if (payload.scope.mediumId !== mediumId) return;

        onStreakUpdated?.({
          previousCurrentStreak: payload.streak.previousCurrentStreak,
          currentStreak: payload.streak.currentStreak,
          previousLongestStreak: payload.streak.previousLongestStreak,
          longestStreak: payload.streak.longestStreak,
          bonusAwarded: payload.streak.bonusAwarded,
          bonusType: payload.streak.bonusType,
          updatedAt: payload.streak.updatedAt,
        });

        queryClient.invalidateQueries({
          queryKey: gamificationPointsQueryKey(terreiroId, mediumId),
        });
        queryClient.invalidateQueries({
          queryKey: gamificationSummaryQueryKey(terreiroId, mediumId),
        });
      },
    );

    const unsubscribeMilestoneReached = wsService.onGamificationMilestoneReached(
      (message) => {
        const payload = (message as WSMessage<GamificationMilestoneReachedPayload>)
          .payload;
        if (!payload) return;
        if (payload.scope.terreiroId !== terreiroId) return;
        if (payload.scope.mediumId !== mediumId) return;

        const now = Date.now();
        const isDuplicate = isDuplicateWithinCooldown(
          lastMilestoneRef.current,
          { points: payload.milestone.points },
          now,
          milestoneCooldownMs,
          (left, right) => left.points === right.points,
        );

        if (isDuplicate) return;

        onMilestoneReached?.({
          points: payload.milestone.points,
          label: payload.milestone.label,
          previousTotalPoints: payload.milestone.previousTotalPoints,
          currentTotalPoints: payload.milestone.currentTotalPoints,
          reachedAt: payload.milestone.reachedAt,
        });

        lastMilestoneRef.current = {
          key: { points: payload.milestone.points },
          at: now,
        };

        queryClient.invalidateQueries({
          queryKey: gamificationPointsQueryKey(terreiroId, mediumId),
        });
        queryClient.invalidateQueries({
          queryKey: gamificationSummaryQueryKey(terreiroId, mediumId),
        });
        queryClient.invalidateQueries({
          queryKey: gamificationHistoryQueryKey(mediumId),
        });
      },
    );

    return () => {
      unsubscribePointsUpdated();
      unsubscribeBadgeEarned();
      unsubscribeStreakUpdated();
      unsubscribeMilestoneReached();
    };
  }, [
    enabled,
    mediumId,
    onBadgeEarned,
    onMilestoneReached,
    onRankChange,
    onStreakUpdated,
    queryClient,
    milestoneCooldownMs,
    rankChangeCooldownMs,
    terreiroId,
  ]);
}

export default useGamificationRealtimeSync;
